import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { categories } from '@/mocks/categories';
import { shows } from '@/mocks/shows';
import type { Show } from '@/mocks/shows';

function ShowCard({ show }: { show: Show }) {
  const formattedHeat =
    show.heat >= 10000 ? `${(show.heat / 10000).toFixed(0)}万` : show.heat;

  return (
    <Link
      to={`/category/${show.categorySlug}`}
      className="group cursor-pointer"
    >
      <div className="relative overflow-hidden rounded-lg aspect-[3/4] mb-3">
        <img
          src={show.cover}
          alt={show.title}
          title={`${show.title} - 极速影院免费综艺节目在线观看`}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
          loading="lazy"
        />
        <div className="absolute top-2 right-2 bg-primary-500 text-white text-xs font-bold px-2 py-1 rounded-md whitespace-nowrap">
          {show.rating}
        </div>
      </div>
      <h3 className="text-sm font-semibold text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-1 mb-1">
        {show.title}
      </h3>
      <div className="flex items-center gap-2 text-xs text-foreground-500">
        <span className="whitespace-nowrap">{show.host}</span>
        <span className="text-foreground-300">·</span>
        <span className="flex items-center gap-1 whitespace-nowrap">
          <i className="ri-fire-line text-accent-500"></i>
          {formattedHeat}
        </span>
      </div>
    </Link>
  );
}

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const currentCategory = categories.find((c) => c.slug === slug);
  const filteredShows = slug
    ? shows.filter((s) => s.categorySlug === slug)
    : shows;

  const otherCategories = categories.filter((c) => c.slug !== slug);

  if (!currentCategory && slug) {
    return (
      <>
        <ScrollToTop />
        <div className="min-h-screen bg-background-50">
          <Navbar />
          <div className="pt-24 pb-12 text-center">
            <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-200">
              <i className="ri-emotion-sad-line text-3xl text-foreground-400"></i>
            </div>
            <h1 className="font-heading text-2xl text-foreground-900 mb-2">
              分类不存在
            </h1>
            <p className="text-foreground-600 mb-6">您访问的综艺分类不存在或已下架</p>
            <Link
              to="/"
              className="inline-flex px-5 py-2.5 bg-primary-500 hover:bg-primary-600 text-white rounded-full font-semibold text-sm transition-all cursor-pointer whitespace-nowrap"
            >
              返回首页
            </Link>
          </div>
          <Footer />
        </div>
      </>
    );
  }

  const displayCategory = currentCategory || categories[0];
  const displayShows = filteredShows.length > 0 ? filteredShows : shows.slice(0, 8);

  const breadcrumbItems = [
    { label: '首页', to: '/' },
    { label: displayCategory.name },
  ];

  const itemListSchema = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    itemListElement: displayShows.map((show, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      item: {
        '@type': 'CreativeWork',
        name: show.title,
        description: show.description,
        url: `https://www.jisucinema.com/category/${show.categorySlug}`,
      },
    })),
  };

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json">{JSON.stringify(itemListSchema)}</script>

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <div className="pt-16">
          {/* Category Header */}
          <section className="w-full px-4 md:px-6 pt-8 pb-6">
            <div className="max-w-7xl mx-auto">
              <Breadcrumb items={breadcrumbItems} />
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mt-2">
                <div>
                  <h1 className="font-heading text-3xl md:text-4xl text-foreground-900">
                    {displayCategory.name}
                  </h1>
                  <p className="text-sm text-foreground-600 mt-1">
                    {displayCategory.description}
                  </p>
                </div>
                <div className="text-sm text-foreground-500">
                  共 <strong className="text-primary-500">{displayShows.length}</strong> 个节目
                </div>
              </div>
            </div>
          </section>

          {/* Category Tabs */}
          <section className="w-full px-4 md:px-6 pb-4">
            <div className="max-w-7xl mx-auto">
              <div className="flex items-center gap-2 overflow-x-auto pb-2 flex-wrap">
                {categories.map((cat) => (
                  <Link
                    key={cat.id}
                    to={`/category/${cat.slug}`}
                    className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all cursor-pointer ${
                      cat.slug === slug
                        ? 'bg-primary-500 text-white'
                        : 'bg-background-100 text-foreground-700 hover:bg-background-200'
                    }`}
                  >
                    <i className={`${cat.icon} text-sm`}></i>
                    {cat.name}
                  </Link>
                ))}
              </div>
            </div>
          </section>

          {/* Shows Grid */}
          <section className="w-full px-4 md:px-6 py-6">
            <div className="max-w-7xl mx-auto">
              {displayShows.length > 0 ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
                  {displayShows.map((show) => (
                    <ShowCard key={show.id} show={show} />
                  ))}
                </div>
              ) : (
                <div className="text-center py-16">
                  <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-200">
                    <i className="ri-search-line text-2xl text-foreground-400"></i>
                  </div>
                  <p className="text-foreground-600">该分类暂无节目，请查看其他分类</p>
                </div>
              )}
            </div>
          </section>

          {/* Other Categories */}
          {otherCategories.length > 0 && (
            <section className="w-full px-4 md:px-6 py-10 bg-background-100">
              <div className="max-w-7xl mx-auto">
                <h2 className="font-heading text-xl text-foreground-900 mb-5">
                  探索其他分类
                </h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                  {otherCategories.map((cat) => (
                    <Link
                      key={cat.id}
                      to={`/category/${cat.slug}`}
                      className="flex items-center gap-3 p-4 rounded-lg bg-background-50 hover:bg-primary-50 transition-all cursor-pointer"
                    >
                      <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100">
                        <i className={`${cat.icon} text-primary-500`}></i>
                      </div>
                      <div>
                        <div className="text-sm font-semibold text-foreground-800 whitespace-nowrap">
                          {cat.name}
                        </div>
                        <div className="text-xs text-foreground-500">
                          {
                            shows.filter((s) => s.categorySlug === cat.slug).length
                          }{' '}
                          个节目
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </section>
          )}
        </div>

        <Footer />
      </div>
    </>
  );
}